import { h, Component } from 'preact';
import s from 'components/bolt/hub/components/heroCarousel/heroCarousel.sss';
import { sortObjectByOrder, accessNested, joinClasses } from 'utils/utils';
import { navigateInternally, splitQueryAndPath, shouldShowShowtimes } from 'utils/bolt';

import Transition from 'components/core/transition/transition';
import Store from 'store/store';
import HTML from 'components/core/html/html';
import { scale } from 'utils/math';
import HubTextOverlay from 'components/bolt/hub/components/hubTextOverlay/hubTextOverlay';

export default class HeroCarousel extends Component {
	constructor(props) {
		super(props);

		this.state = {
			activeIndex: 0,
			transitioning: false
		};

		const content = this.props.data?.content || {};
		this.slides = sortObjectByOrder(content);
		this.slideRefs = {};

		this.startIterating = this.startIterating.bind(this);
		this.handleTrailerToggle = this.handleTrailerToggle.bind(this);
		this.renderButtons = this.renderButtons.bind(this);
		this.renderButton = this.renderButton.bind(this);
		this.iterate = this.iterate.bind(this);
		this.handleTouchStart = this.handleTouchStart.bind(this);
		this.handleTouchEnd = this.handleTouchEnd.bind(this);
		this.handleTouchMove = this.handleTouchMove.bind(this);
	}

	componentDidMount() {
		this.startIterating();
		// This prevents the featured VideoOverlay from displaying.
		// Store.get().set({ trailerOverlay: { overlayOpen: false } });
		Store.on('TOGGLE_TRAILER_OVERLAY', this.handleTrailerToggle);
	}

	componentWillReceiveProps(nextProps) {
		if (this.props.data !== nextProps.data) {
			const content = nextProps.data?.content || {};
			this.slides = sortObjectByOrder(content);
		}
	}

	componentDidUpdate(prevProps, prevState) {
		const oldIndex = prevState.activeIndex;
		const newIndex = this.state.activeIndex;
		if (oldIndex !== newIndex) {
			const previousFocusedButton = this.slideRefs[`slide${oldIndex}`]?.contains(document.activeElement);
			if (previousFocusedButton) {
				this.slideRefs[`slide${newIndex}`]?.querySelector('button')?.focus();
			}
		}
	}

	componentWillUnmount() {
		clearTimeout(this.transitioningTimeout);
		clearTimeout(this.nextTimeout);
		Store.off('TOGGLE_TRAILER_OVERLAY', this.handleTrailerToggle);
	}

	handleTouchStart(e) {
		this.xDown = e.touches[0].clientX;
		this.yDown = e.touches[0].clientY;
	}

	handleTouchEnd() {
		this.direction = null;
	}

	handleTouchMove(e) {
		this.xMove = e.touches[0].clientX;
		this.yMove = e.touches[0].clientY;

		const verticalSwipe = this.yMove - this.yDown;
		const horizontalSwipe = this.xMove - this.xDown;
		const angle = horizontalSwipe / verticalSwipe;

		const direction = horizontalSwipe > 0 ? -1 : 1;

		if (angle > 1 || angle < -1) {
			e.preventDefault();
			if (!this.direction || direction !== this.direction) {
				this.iterate(direction);
				this.direction = direction;
			}
		}
	}

	handleTrailerToggle(open) {
		if (open) {
			clearTimeout(this.nextTimeout);
		} else {
			this.startIterating();
		}
	}

	startIterating() {
		const { interval = this.defaultInterval || 7000 } = this.props;
		clearTimeout(this.nextTimeout);
		this.nextTimeout = setTimeout(() => {
			this.iterate(1);
		}, interval);
	}

	iterate(direction, forceIndex) {
		const { activeIndex } = this.state;
		let newIndex = (typeof forceIndex !== 'undefined') ? forceIndex : activeIndex + direction;

		if (newIndex < 0) {
			newIndex = this.slides.length - 1;
		} else if (newIndex === this.slides.length) {
			newIndex = 0;
		}

		this.setState({
			activeIndex: newIndex,
			transitioning: true
		});

		this.transitioningTimeout = setTimeout(() => {
			this.setState({ transitioning: false });
		}, 1200);

		this.startIterating();
	}

	trackHeroClick(title, label, link) {
		Store.emit('HUB_HERO_CAROUSEL_ENGAGEMENT_CTA_CLICKED', { tileTitle: title, buttonLabel: label, destination: link });
	}

	onSlideClick({ target }, index, slidePath, slideTitle, textOverlay, textOverlayId, videoOverlay, sameWindow) {
		if (!this.buttons?.contains(target)) {
			Store.emit('HUB_HERO_CAROUSEL_ENGAGEMENT_TILE_CLICKED', { tileTitle: slideTitle, destination: slidePath });
		}
		if (
			this.state.hasTextOverlayOpen ||
			(target?.className?.includes && target.className.includes('hubTextOverlay'))
		) return;

		const tile = this.slideRefs[`slide${index}`];

		if (tile.contains(target) && (this.buttons && !this.buttons.contains(target))) {
			if (textOverlay && textOverlayId) {
				this.toggleTextOverlay(true, textOverlayId);
				return;
			}
			if (videoOverlay) {
				return Store.emit('ENABLE_TRAILER_OVERLAY', videoOverlay.video);
			}
			if (slidePath) {
				if (slidePath.startsWith('http') || slidePath.startsWith('mailto:')) {
					let link = document.createElement('a');
					link.href = slidePath;
					link.target = '_blank';
					link.style.display = 'none';
					if (sameWindow) {
						link.target = '_self';
					}
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
				} else {
					const { path, query } = splitQueryAndPath(slidePath);
					navigateInternally(path, { queryString: query });
				}
			}
		}
	}

	toggleTextOverlay(open, textOverlayId) {
		if (open) {
			clearTimeout(this.nextTimeout);
		} else {
			this.startIterating();
		}
		this.setState({
			hasTextOverlayOpen: open,
			[`textOverlay-${textOverlayId}-show`]: open
		});
	}


	renderTextOverlay(textOverlay, textOverlayId) {
		if (!textOverlay) return;
		const show = this.state[`textOverlay-${textOverlayId}-show`];

		return (
			<HubTextOverlay
				content={textOverlay}
				show={show}
				closeOverlay={() => this.toggleTextOverlay(false, textOverlayId)}
			/>
		);
	}

	renderBackgrounds() {
		const isDesktopSize = accessNested(this.props, 'client.isDesktopSize', false);
		const resolution = isDesktopSize ? 'desktop' : 'mobile';
		const deviceSize = accessNested(this.props, 'client.deviceSize', 'desktop');

		const { activeIndex } = this.state;
		const slide = this.slides[activeIndex];
		// NOTE: should we completely skip the render if no slides are available ?
		const asset = slide?.background?.[resolution] || slide?.background?.desktop || slide?.background?.mobile || '';
		const isVideo = asset.endsWith('.mp4') || asset.endsWith('.webm');
		const backgroundAlignment = slide?.background?.backgroundAlignment || 'center';
		const backgroundHeight = slide?.background?.height || {};
		const height = backgroundHeight?.[deviceSize] ?? '';
		const hasGradient = accessNested(this.props, 'appData.heroCarousel.hasGradient', false);
		const sameWindow = slide?.sameWindow || false;

		const textOverlay = slide?.textOverlay?.content;
		const textOverlayId = `slide${activeIndex}`;
		const videoOverlay = slide?.videoOverlay;

		return (
			<Transition duration={800} class={s.transitionClass}>
				<div
					class={joinClasses(s.slide, s[slide?.id], slide?.dark && s.dark, slide?.path && s.clickable)}
					id={`slide${activeIndex}`}
					onClick={e => this.onSlideClick(e, activeIndex, slide?.path, slide?.title, textOverlay, textOverlayId, videoOverlay, sameWindow)}
					ref={ref => this.slideRefs[`slide${activeIndex}`] = ref}
					style={{ height }}
				>
					{!!asset && !isVideo && <img src={asset} alt={slide?.background?.alt} style={{ objectPosition: backgroundAlignment }} />}
					{!!asset && isVideo && <video class={s.video} src={asset} style={{ objectPosition: backgroundAlignment }} autoplay loop muted playsinline />}
					{hasGradient && <div class={s.gradient} />}
					{this.renderContent(slide)}
					{textOverlay && this.renderTextOverlay(textOverlay, textOverlayId)}
				</div>
			</Transition>
		);
	}

	renderContent(content) {
		if (!content) return;
		const deviceSize = accessNested(this.props, 'client.deviceSize', 'mobile');
		const resolution = deviceSize === 'desktop' ? 'desktop' : 'mobile';
		const logos = sortObjectByOrder(content.logos);
		const { activeIndex } = this.state;
		const slide = this.slides[activeIndex];
		const hubActivePercentage = this.props.store?.hubActivePercentage || 0;

		return (
			<div class={s.content} style={{ opacity: hubActivePercentage <= 0.5 ? 0 : 1, transform: hubActivePercentage >= 0.5 ? 'translateX(0)' : 'translateX(100%)' }}>
				{content.tt && <img class={s.tt} src={content.tt[resolution]} alt={content.tt.alt} />}
				<HTML tag="div" class={s.title} markdown>{content?.title}</HTML>
				{content?.subtitle && <HTML tag="div" class={s.subtitle} markdown>{content?.subtitle}</HTML>}
				<HTML tag="div" class={joinClasses(s.description, !content?.title && s.noTitle)} markdown>{content?.description}</HTML>
				<div class={s.logos}>
					{logos.map(logo => <img class={s.logo} src={logo.src} alt={logo.alt} />)}
				</div>
				{slide.buttons && this.renderButtons(slide.buttons, slide, activeIndex)}
			</div>
		);
	}

	renderBreadcrumbs() {
		if (this.slides.length <= 1) return;

		let { activeIndex } = this.state;
		const darkCrumbs = this.slides[activeIndex]?.darkCrumbs || this.slides[activeIndex]?.dark;
		return (
			<div class={s.breadcrumbs}>
				{this.slides.map((crumb, index) => (
					<button
						class={joinClasses(s.crumb, activeIndex === index && s.active, darkCrumbs && s.dark)}
						onClick={() => this.iterate(null, index)}
					/>
				))}
			</div>
		);
	}

	renderButton(button, title, slideIndex, index, has3OrMoreButtons) {
		const showtimesLive = shouldShowShowtimes();
		const store = Store.get() || {};
		const copy = store?.appData?.copy?.baseCopy || {};

		let overriddenBtnCopy;
		let overriddenBtnPath;

		if (button.mimicPersistentBtn && showtimesLive) {
			overriddenBtnCopy = copy.$GET_TICKETS;
			overriddenBtnPath = 'boltShowtimes';
		} else if (button.mimicPersistentBtn && !showtimesLive) {
			overriddenBtnCopy = copy.$CALENDAR_REMINDER_BTN_COPY;
			overriddenBtnPath = '@calendarreminder';
		}
		const link = overriddenBtnPath || button.link || '';
		const textOverlay = button.textOverlay?.content;
		const isMobile = accessNested(this.props, 'client.deviceSize', 'mobile') === 'mobile';

		// legacy btnCopy? Can probably remove soon
		const buttonLabel = overriddenBtnCopy || copy[button.label] || button.label || copy[button.btnCopy] || button.btnCopy || '';

		if (link.startsWith('http')) {
			return (
				<a
					href={link}
					target={button.sameWindow ? '_self' : '_blank'}
					class={joinClasses(s.cta, s.watch)}
					onClick={() => this.trackHeroClick(title, buttonLabel, link)}
				>
					{buttonLabel}
				</a>
			);
		}

		return (
			<button
				class={joinClasses(s.cta, s.watch, (has3OrMoreButtons && isMobile) && s.has3OrMoreButtons)}
				onClick={() => {
					const target = overriddenBtnPath || button.pageId || link;
					let targetTrack = textOverlay ? 'text overlay' : button.videoOverlay?.video ? 'video overlay' : target;
					this.trackHeroClick(title, button.label, targetTrack);
					if (textOverlay) {
						const textOverlayId = `slide${slideIndex}-button${index}`;
						this.toggleTextOverlay(true, textOverlayId);
						return;
					}
					if (button.videoOverlay?.video) {
						return Store.emit('ENABLE_TRAILER_OVERLAY', button.videoOverlay.video);
					}
					if (target) {
						const { path, query } = splitQueryAndPath(target);
						navigateInternally(path, { queryString: query, instant: false });
					}
				}}
			>
				{buttonLabel}
			</button>
		);
	}

	renderButtons(buttonsContent, slide, slideIndex) {
		// Should we use components/bolt/hub/components/buttons ?
		const buttonsArray = sortObjectByOrder(buttonsContent);
		const has3OrMoreButtons = buttonsArray.length >= 3;
		const buttons = buttonsArray.map((button, i) => this.renderButton(button, slide.title, slideIndex, i, has3OrMoreButtons));

		const buttonTextOverlays = buttonsArray.map((button, i) => {
			return this.renderTextOverlay(button.textOverlay?.content, `slide${slideIndex}-button${i}`);
		}).filter(x => x);

		return (
			<div className={s.ctas} ref={ref => this.buttons = ref}>
				{buttons}
				{buttonTextOverlays?.length > 0 && buttonTextOverlays}
			</div>
		);
	}

	render() {
		const { activeIndex } = this.state;
		const slide = this.slides[activeIndex];
		const { visible, hide } = this.props;
		const hubActivePercentage = this.props.store?.hubActivePercentage ?? 1;
		const scrollFadeOpacity = scale(hubActivePercentage, .8, .6, 0, .6, true);

		return (
			<div
				class={joinClasses(
					s.wrapper,
					(slide?.path || slide?.videoOverlay || slide?.textOverlay) && s.clickable,
					visible && s.visible,
					hide && s.hide
				)}
				data-attribute="boltHubPageCarouselHero"
				data-hash-wrapper={this.props.data?.anchor || 'top'}
				id={this.props.data?.anchor || 'top'}
				onTouchStart={this.handleTouchStart}
				onTouchMove={this.handleTouchMove}
				onTouchEnd={this.handleTouchEnd}
			>
				<div class={s.scrollFade} style={{ opacity: scrollFadeOpacity }} />
				<div class={s.container}>
					{this.renderBackgrounds()}
					{this.renderBreadcrumbs()}
				</div>
			</div>
		);
	}
}

